// src/pages/Onboarding/slides/DisplayChartSlides/MedicalBackgroundSlide.js
import React from 'react';
import SlideShell from '../../SlideShell';
import InfoTooltip from '../../../../components/InfoTooltip';
import keloidScarImage from '../../../../images/keloid-scar.jpg';

// Animation keyframes (scoped)
const FadeStyles = () => (
  <style>{`
    @keyframes fadeIn {
      from { opacity: 0; transform: scale(0.985) }
      to   { opacity: 1; transform: scale(1) }
    }
    .fade-in { animation: fadeIn 280ms ease-out both; }
    @media (prefers-reduced-motion: reduce) { .fade-in { animation: none; } }

    /* --- Slide animations for card transition --- */
    @keyframes outRight {
      from { transform: translateX(0%); opacity: 1; }
      to   { transform: translateX(120%); opacity: 0; }
    }
    @keyframes inFromLeft {
      from { transform: translateX(-100%); opacity: 0.8; }
      to   { transform: translateX(0%); opacity: 1; }
    }
    @keyframes outLeft {
      from { transform: translateX(0%); opacity: 1; }
      to   { transform: translateX(-120%); opacity: 0; }
    }
    @keyframes inFromRight {
      from { transform: translateX(100%); opacity: 0.8; }
      to   { transform: translateX(0%); opacity: 1; }
    }

    .card-anim {
      will-change: transform, opacity;
      backface-visibility: hidden;
      -webkit-backface-visibility: hidden;
      transform: translateZ(0);
    }

    /* Discard animations (duration is configurable via CSS var) */
    .out-right { animation: outRight var(--discard-anim-ms, 520ms) cubic-bezier(0.22, 1, 0.36, 1) both; }
    .in-left   { animation: inFromLeft var(--discard-anim-ms, 520ms) cubic-bezier(0.22, 1, 0.36, 1) both; }
    .out-left  { animation: outLeft var(--discard-anim-ms, 520ms) cubic-bezier(0.22, 1, 0.36, 1) both; }
    .in-right  { animation: inFromRight var(--discard-anim-ms, 520ms) cubic-bezier(0.22, 1, 0.36, 1) both; }

    @media (prefers-reduced-motion: reduce) {
      .out-right, .in-left, .out-left, .in-right { animation-duration: 1ms; }
    }
  `}</style>
);

// Question card data
const MEDICAL_QUESTIONS = [
  {
    id: 'genetic_heart_conditions',
    image: require('../../../../images/medical_background_images/heart-attack.png'),
    title: 'Do you have any genetic heart conditions?',
    subtext: 'Any heart conditions in your family?',
  },
  {
    id: 'allergies',
    image: require('../../../../images/medical_background_images/peanut-free.png'),
    title: 'Do you have any allergies?',
    subtext: 'Food, medicine, latex etc.',
  },
  {
    id: 'chronic_illness',
    image: require('../../../../images/medical_background_images/diabetes.png'),
    title: 'Do you have any chronic illness?',
    subtext: 'Heart disease, diabetes etc.',
  },
  {
    id: 'regular_medication',
    image: require('../../../../images/medical_background_images/pill-medicine.png'),
    title: 'Do you use any medication regularly?',
    subtext: 'Insulin, anticoagulants etc.',
  },
  {
    id: 'contagious_disease',
    image: require('../../../../images/medical_background_images/virus.png'),
    title: 'Do you have or had any contagious disease?',
    subtext: 'Hepatitis A/B/C, HIV etc.',
  },
  {
    id: 'keloid_scars',
    image: require('../../../../images/medical_background_images/scar-hand.png'),
    title: 'Do you have any keloid scars on your skin?',
    subtext: 'Keloid scars look like this.',
  },
  {
    id: 'needle_phobia',
    image: require('../../../../images/medical_background_images/syringe.png'),
    title: 'Do you have a needle phobia?',
    subtext: null,
  },
  {
    id: 'low_pain_threshold',
    image: require('../../../../images/medical_background_images/band-aid.png'),
    title: 'Do you have a low pain threshold?',
    subtext: null,
  },
  {
    id: 'smoker',
    image: require('../../../../images/medical_background_images/smoking.png'),
    title: 'Are you a smoker?',
    subtext: null,
  },
  {
    id: 'drug_addiction',
    image: require('../../../../images/medical_background_images/drug-bottle.png'),
    title: 'Are you addicted to any drugs?',
    subtext: null,
  },
  {
    id: 'previous_surgery',
    image: require('../../../../images/medical_background_images/scissors.png'),
    title: 'Have you had any surgery before?',
    subtext: null,
  },
  {
    id: 'recent_anesthesia',
    image: require('../../../../images/medical_background_images/sleeping-mask.png'),
    title: 'Have you had local or general anesthesia in the last 2 years?',
    subtext: null,
  },
  {
    id: 'anemia_diagnosis',
    image: require('../../../../images/medical_background_images/blood-cell.png'),
    title: 'Have you ever been diagnosed with anemia?',
    subtext: null,
  },
];

const COMPLETION_CARD = {
  image: require('../../../../images/medical_background_images/thumbs-up.png'),
  title: 'All done!',
  subtitle: 'Your doctor will provide the conclusive diagnosis.',
};

export default function MedicalBackgroundSlide({
  title,
  subtitle,
  currentStep,
  totalSteps,
  onNext,
  onBack,
  animationDirection = 'forward',
  isTransitioning = false,
  theme = 'light',
  initialAnswers = {},
  onAutoSave = null,
  discardAnimationMs = 700,
}) {
  const isDark = theme === 'dark';
  const pageBg = isDark ? 'bg-mm_black' : 'bg-white';

  const [index, setIndex] = React.useState(0);
  const [answers, setAnswers] = React.useState(() => {
    const initial = {};
    MEDICAL_QUESTIONS.forEach((q) => {
      initial[q.id] = initialAnswers[q.id] ?? null;
    });
    return initial;
  });
  const autoSaveRef = React.useRef(null);

  React.useEffect(() => {
    const initial = {};
    MEDICAL_QUESTIONS.forEach((q) => {
      initial[q.id] = initialAnswers[q.id] ?? null;
    });
    setAnswers(initial);
    autoSaveRef.current = JSON.stringify(initial);
  }, [initialAnswers]);

  const total = MEDICAL_QUESTIONS.length;
  const isComplete = index >= total;
  const currentQuestion = isComplete ? null : MEDICAL_QUESTIONS[index];

  // Queue of outgoing animated cards
  const [outgoing, setOutgoing] = React.useState([]);
  const idRef = React.useRef(0);
  const [completed, setCompleted] = React.useState(false);
  const [showCompletionCard, setShowCompletionCard] = React.useState(false);

  const [contentKey, setContentKey] = React.useState(0);
  const [showContent, setShowContent] = React.useState(false);
  
  React.useEffect(() => {
    if (isTransitioning) {
      setShowContent(false);
      return;
    }
    const id = setTimeout(() => {
      setContentKey((k) => k + 1);
      setShowContent(true);
    }, 200);
    return () => clearTimeout(id);
  }, [isTransitioning]);

  const commitAutoSave = React.useCallback((nextAnswers) => {
    if (typeof onAutoSave !== 'function') return;
    const serialized = JSON.stringify(nextAnswers);
    if (autoSaveRef.current === serialized) return;
    autoSaveRef.current = serialized;
    onAutoSave(nextAnswers);
  }, [onAutoSave]);

  React.useEffect(() => {
    commitAutoSave(answers);
  }, [answers, commitAutoSave]);

  const processAnswer = React.useCallback((answer) => {
    if (index >= total) return;
    const question = MEDICAL_QUESTIONS[index];
    const direction = 'left'; // Always discard to the left
    const id = ++idRef.current;

    // Animate this card out
    setOutgoing((prev) => [...prev, { id, question, direction }]);

    // Record answer
    setAnswers((prev) => ({
      ...prev,
      [question.id]: answer,
    }));

    // Advance deck
    setIndex((i) => i + 1);
  }, [index, total]);

  // Cleanup each outgoing card after animation
  const handleOutgoingEnd = React.useCallback((id) => {
    setOutgoing((prev) => prev.filter((item) => item.id !== id));
  }, []);

  // When deck is exhausted, show completion card then auto-advance
  React.useEffect(() => {
    if (!completed && isComplete) {
      setShowCompletionCard(true);
      const timer = setTimeout(() => {
        onNext?.(answers);
        setCompleted(true);
      }, 1200); // Brief delay to show "All done!"
      return () => clearTimeout(timer);
    }
  }, [isComplete, completed, answers, onNext]);

  const buttonsDisabled = isComplete || showCompletionCard;

  const footer = (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <button
          onClick={() => processAnswer('yes')}
          disabled={buttonsDisabled}
          className={`
            flex-1 px-4 py-3 rounded-full font-medium text-sm border transition
            focus:outline-none focus:ring-2 focus:ring-offset-2
            ${isDark 
              ? 'text-white border-gray-600 hover:bg-gray-800 focus:ring-gray-500 focus:ring-offset-mm_black' 
              : 'text-mm_black border-[#1f1f1f] hover:bg-gray-100 focus:ring-gray-400 focus:ring-offset-white'}
            ${buttonsDisabled ? 'opacity-40 cursor-not-allowed' : ''}
          `}
        >
          Yes
        </button>

        <button
          onClick={() => processAnswer('no')}
          disabled={buttonsDisabled}
          className={`
            flex-1 px-4 py-3 rounded-full font-medium text-sm border transition
            focus:outline-none focus:ring-2 focus:ring-offset-2
            ${isDark 
              ? 'text-white border-gray-600 hover:bg-gray-800 focus:ring-gray-500 focus:ring-offset-mm_black' 
              : 'text-mm_black border-[#1f1f1f] hover:bg-gray-100 focus:ring-gray-400 focus:ring-offset-white'}
            ${buttonsDisabled ? 'opacity-40 cursor-not-allowed' : ''}
          `}
        >
          No
        </button>
      </div>
      
      <p className={`text-center text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
        If yes, we might ask for further details later on.
      </p>
    </div>
  );

  return (
    <div className={`min-h-screen ${pageBg}`} style={{ ['--discard-anim-ms']: `${discardAnimationMs}ms` }}>
      <SlideShell
        title={title}
        subtitle={subtitle}
        currentStep={currentStep}
        totalSteps={totalSteps}
        onNext={() => onNext?.(answers)}
        onBack={onBack}
        footer={footer}
        theme={theme}
        animationDirection={animationDirection}
        isTransitioning={isTransitioning}
        showProgress={true}
      >
        <FadeStyles />
        {showContent && (
          <div key={contentKey} className="fade-in">
            <div className="relative w-full" style={{ height: 250 }}>
              {/* Current top card (static) - only this bordered box animates */}
              {!showCompletionCard && currentQuestion && (
                <div 
                  className="absolute inset-0 flex flex-col items-center justify-center p-6 bg-white rounded-2xl"
                  style={{ border: '2px solid #a6a6a6' }}
                >
                  <img
                    src={currentQuestion.image}
                    alt=""
                    className="w-16 h-16 object-contain mb-5"
                    loading="eager"
                    decoding="sync"
                  />
                  <h3 className="text-xl font-bold text-center text-mm_black mb-2">
                    {currentQuestion.title}
                  </h3>
                  {currentQuestion.subtext && (
                    <p className="text-sm text-center text-gray-500">
                      {currentQuestion.id === 'keloid_scars' ? (
                        <>
                          Keloid scars look like{' '}
                          <InfoTooltip
                            imageSrc={keloidScarImage}
                            imageAlt="Keloid scar example"
                            mobileTitle="Keloid Scar Example"
                            position="bottom"
                            maxWidth={250}
                            className=""
                          >
                            <span className="underline decoration-dotted underline-offset-2 cursor-pointer">
                              this.
                            </span>
                          </InfoTooltip>
                        </>
                      ) : (
                        currentQuestion.subtext
                      )}
                    </p>
                  )}
                </div>
              )}

              {/* Completion card */}
              {showCompletionCard && (
                <div 
                  className="absolute inset-0 flex flex-col items-center justify-center p-6 bg-white rounded-2xl"
                  style={{ border: '2px solid #a6a6a6' }}
                >
                  <img
                    src={COMPLETION_CARD.image}
                    alt=""
                    className="w-16 h-16 object-contain mb-5"
                    loading="eager"
                    decoding="sync"
                  />
                  <h3 className="text-2xl font-bold text-center text-mm_black mb-2">
                    {COMPLETION_CARD.title}
                  </h3>
                  <p className="text-sm text-center text-gray-500">
                    {COMPLETION_CARD.subtitle}
                  </p>
                </div>
              )}

              {/* Outgoing cards animating - only the bordered boxes */}
              {outgoing.map((item, i) => {
                const animClass = 'out-left'; // Always animate to the left
                return (
                  <div
                    key={item.id}
                    onAnimationEnd={(e) => {
                      if (e.currentTarget !== e.target) return;
                      handleOutgoingEnd(item.id);
                    }}
                    className={`card-anim absolute inset-0 bg-white rounded-2xl ${animClass}`}
                    style={{ zIndex: 10 + i, border: '2px solid #a6a6a6', backgroundColor: '#ffffff' }}
                    aria-hidden
                  >
                    {/* Card content */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center p-6">
                      <img
                        src={item.question.image}
                        alt=""
                        className="w-16 h-16 object-contain mb-5"
                        loading="eager"
                        decoding="sync"
                      />
                      <h3 className="text-xl font-bold text-center text-mm_black mb-2">
                        {item.question.title}
                      </h3>
                      {item.question.subtext && (
                        <p className="text-sm text-center text-gray-500">
                          {item.question.subtext}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className={`mt-3 text-sm text-center ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              {showCompletionCard ? `${total} / ${total}` : `${Math.min(index + 1, total)} / ${total}`}
            </div>
          </div>
        )}
      </SlideShell>
    </div>
  );
}
